<template>
  <div class="card shadow-sm">
    <div class="card-header bg-body-tertiary py-2 d-flex flex-wrap align-items-center justify-content-between gap-2">
      <h6 class="mb-0">
        <i class="bi bi-clock-history me-2"></i>Timeline de Relatórios
      </h6>

      <button
        type="button"
        class="btn btn-sm btn-outline-secondary"
        title="Atualizar histórico persistido"
        @click="emit('atualizar')"
      >
        <i class="bi bi-arrow-clockwise"></i>
      </button>
    </div>

    <div class="card-body p-3">
      <!-- Filtros: período (De/Até) + Erros + Hoje -->
      <div class="row g-2 align-items-end mb-3">
        <div class="col-12 col-md-7">
          <CampoDataIntervalo
            id="filtroDataRelatorios"
            label="Período"
            v-model:modelValueInicio="dataInicioModel"
            v-model:modelValueFim="dataFimModel"
            container-class="seed-timeline__periodo"
          />
        </div>
        <div class="col-12 col-md-5">
          <div class="btn-group btn-group-sm w-100" role="group" aria-label="Filtro de relatórios">
            <button
              type="button"
              class="btn"
              :class="somenteErros ? 'btn-danger' : 'btn-outline-secondary'"
              @click="emit('update:somenteErros', !somenteErros)"
            >
              <i class="bi bi-exclamation-triangle-fill me-1"></i>Erros
            </button>
            <button
              type="button"
              class="btn"
              :class="filtroHojeAtivo ? 'btn-primary' : 'btn-outline-secondary'"
              @click="emit('aplicar-hoje')"
            >
              <i class="bi bi-calendar-day me-1"></i>Hoje
            </button>
          </div>
        </div>
      </div>

      <div v-if="filtrados.length === 0" class="text-center text-body-secondary py-4">
        <i class="bi bi-inbox fs-3 d-block mb-2"></i>
        <p class="mb-0 small">Nenhum relatório para o filtro selecionado.</p>
      </div>

      <div v-else class="seed-timeline">
        <button
          v-for="(l, index) in filtrados"
          :key="l.id"
          type="button"
          class="seed-timeline__badge"
          :class="[classeBadge(l), { 'seed-timeline__badge--ativo': l.id === selecionadoId }]"
          :title="`${l.nomeSeed} — ${l.titulo}`"
          @click="emit('selecionar', l.id)"
        >
          <span class="seed-timeline__num">{{ index + 1 }}</span>
          <i :class="[l.icone || 'bi-box', 'seed-timeline__icone']"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import CampoDataIntervalo from '@/components/base/campos/CampoDataIntervalo.vue'
import { dataHojeBR } from '@/utils/useDataBR'

const props = defineProps({
  lotes: { type: Array, default: () => [] },
  selecionadoId: { type: [String, Number], default: null },
  somenteErros: { type: Boolean, default: false },
  dataInicio: { type: String, default: '' },
  dataFim: { type: String, default: '' }
})

const emit = defineEmits([
  'selecionar',
  'atualizar',
  'update:somenteErros',
  'update:dataInicio',
  'update:dataFim',
  'aplicar-hoje'
])

// A lista de lotes já chega filtrada pelo composable (useSeeds). Aqui apenas
// invertemos para exibir em ordem cronológica crescente (mais antigo à esquerda).
const filtrados = computed(() => [...props.lotes].reverse())

// Indica que o período ativo corresponde exatamente ao dia de hoje,
// destacando o botão "Hoje" como filtro aplicado (padrão ao abrir a Central).
const filtroHojeAtivo = computed(() => {
  const hoje = dataHojeBR()
  return props.dataInicio === hoje && props.dataFim === hoje
})

// Writable computeds para os v-model do CampoDataIntervalo.
const dataInicioModel = computed({
  get: () => props.dataInicio,
  set: (v) => emit('update:dataInicio', v)
})

const dataFimModel = computed({
  get: () => props.dataFim,
  set: (v) => emit('update:dataFim', v)
})

function classeBadge (r) {
  if (r.status === 'erro') return 'seed-timeline__badge--erro'
  if (r.status === 'aviso') return 'seed-timeline__badge--aviso'
  return 'seed-timeline__badge--sucesso'
}
</script>

<style scoped>
/* Alinha o input do período com os botões (remove o mb-3 padrão do CampoDataIntervalo) */
:deep(.seed-timeline__periodo) {
  margin-bottom: 0 !important;
}

.seed-timeline {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  overflow-x: auto;
  padding: 0.5rem 0.25rem;
}

.seed-timeline__badge {
  position: relative;
  flex: 0 0 auto;
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 50%;
  border: 2px solid var(--bs-border-color);
  background-color: var(--bs-body-bg);
  color: var(--bs-body-color);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.seed-timeline__badge:hover {
  transform: translateY(-2px);
  box-shadow: 0 0.25rem 0.5rem rgba(0, 0, 0, 0.15);
}

.seed-timeline__badge--sucesso {
  border-color: var(--bs-success);
  color: var(--bs-success);
}

.seed-timeline__badge--aviso {
  border-color: var(--bs-warning);
  color: var(--bs-warning);
}

.seed-timeline__badge--erro {
  border-color: var(--bs-danger);
  color: var(--bs-danger);
}

.seed-timeline__badge--ativo {
  box-shadow: 0 0 0 0.2rem var(--bs-primary-bg-subtle);
  background-color: var(--bs-tertiary-bg);
}

.seed-timeline__num {
  font-size: 0.7rem;
  font-weight: 700;
  line-height: 1;
}

.seed-timeline__icone {
  font-size: 0.85rem;
  line-height: 1;
}
</style>
